import {
  Badge,
  Checkbox,
  Fieldset,
  Group,
  Modal,
  Stack,
  Tabs,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { ConfigFlow } from "./-components/config-flow"
import { ReactFlowProvider } from "reactflow"
import { DefaultButton, PrimaryButton } from "../../../../-components/Buttons"
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../utils/form-submission-notifications"
import { useRouter } from "@tanstack/react-router"
import { Page } from "../../../../-components/Page"
import { Form } from "../../../../-components/typed-form"
import { DottedMenu } from "../../../../-components/dotted-menu"
import { Text } from "@mantine/core"
import { useClipboard } from "@mantine/hooks"
import { AbsenceConfigFormInputs } from "./-components/shared-form-inputs"
import { usePostMutation } from "../../../../typed-client/hooks/use-post-mutation"
import { P, match } from "ts-pattern"
import { IconCheck } from "@tabler/icons-react"
import { notifications } from "@mantine/notifications"
import { AbsentApprovalConfig } from "../../../../@client/Theasoft_Absences_Config"
import { useTranslations } from "../../../../utils/use-t"
import { AbsenceConfigContextProvider } from "./-components/AbsenceConfigContextProvider"
import { useState } from "react"

import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute(
  "/app/admin-settings/absences/$id"
)({
  component: AbsenceConfigDetailPage,
})

export function AbsenceConfigDetailPage() {
  const translationObjects = useTranslations()
  const params = Route.useParams()
  const id = parseInt(params.id)
  const {
    refetch,
    data: {
      config: { data, isActive },
      canModify,
    },
  } = useSuspenseQueryGet("/api/LeaveRequestConfig/GetById", {
    absenceWorkflowConfigId: id,
  })
  const readonlyTooltip = match(canModify)
    .with({ Case: "Denied", Fields: P.select() }, (msg) => msg)
    .with({ Case: "Granted" }, () => "")
    .exhaustive()

  const deleteConfig = usePostMutation("/api/LeaveRequestConfig/Delete", {
    onError: showErrorNotification,
  })
  const activate = usePostMutation("/api/LeaveRequestConfig/SetIsActive", {
    onError: showErrorNotification,
    onSuccess: () => refetch(),
  })
  const updateConfig = usePostMutation("/api/LeaveRequestConfig/Update", {
    onSuccess: () =>
      showSuccessNotification(translationObjects.appServer.t("Saved")),
    onError: showErrorNotification,
  })
  const clipboard = useClipboard({ timeout: 500 })
  const router = useRouter()
  const [showIds, setShowIds] = useState(false)
  const [showValidationModal, setShowValidationModal] = useState(false)
  const validate = usePostMutation("/api/LeaveRequestConfig/Validate", {
    onSuccess: () => setShowValidationModal(true),
  })
  return (
    <Page
      crumbOverride={() => (
        <Text fw={500} size="xl">
          {data.name}
        </Text>
      )}
    >
      <Modal.Root
        opened={showValidationModal}
        onClose={() => setShowValidationModal(false)}
      >
        <Modal.Overlay />
        <Modal.Content>
          {validate.data
            ? match(validate.data)
                .with({ Case: "Ok" }, () => (
                  <>
                    <Modal.Header>
                      <Modal.Title>
                        <Group>
                          <ThemeIcon color="green">
                            <IconCheck />
                          </ThemeIcon>
                          <Title order={3} c="green">
                            {translationObjects.appServer.t("NoError")}
                          </Title>
                        </Group>
                      </Modal.Title>

                      <Modal.CloseButton />
                    </Modal.Header>
                  </>
                ))
                .with({ Case: "Error" }, ({ Fields: errors }) => (
                  <>
                    <Modal.Header>
                      <Modal.Title>
                        <Title order={3} c="red">
                          {`${errors.length} ${translationObjects.appServer.t("Errors")}:`}
                        </Title>
                      </Modal.Title>

                      <Modal.CloseButton />
                    </Modal.Header>
                    <Modal.Body>
                      {errors.map((v) => (
                        <Group>
                          <Text span c="red">
                            {match(v)
                              .with(
                                { Case: "General", Fields: P.select() },
                                (x) => x
                              )
                              .with(
                                { Case: "Node", Fields: P.select() },
                                (x) => (
                                  <div>
                                    {x.message} ({x.nodeId})
                                  </div>
                                )
                              )
                              .exhaustive()}
                          </Text>
                        </Group>
                      ))}
                    </Modal.Body>
                  </>
                ))
                .exhaustive()
            : null}
        </Modal.Content>
      </Modal.Root>
      <Form<AbsentApprovalConfig, "/api/LeaveRequestConfig/Update">
        url="/api/LeaveRequestConfig/Update"
        transform={(values) => ({
          absentApprovalConfigId: id,
          data: values,
        })}
        initialValues={data}
        options={{
          onSuccess: (result) => {
            if (result.workflowIsValid) {
              showSuccessNotification(translationObjects.appServer.t("Success"))
            } else {
              notifications.show({
                message: translationObjects.appServer.t("InvalidWorkflow"),
                title: translationObjects.appServer.t("Warning"),
                color: "orange",
              })
            }

            refetch()
          },
          onError: showErrorNotification,
        }}
      >
        {(f, _) => (
          <>
            <Group justify="space-between" align="center">
              {isActive ? (
                <Badge>
                  Status: {translationObjects.appServer.t("Active")}
                </Badge>
              ) : (
                <Badge color="gray">
                  Status: {translationObjects.appServer.t("Inactive")}
                </Badge>
              )}
              <Group mb="xs" justify="end">
                <DefaultButton
                  key="validate"
                  loading={validate.isPending}
                  onClick={() => validate.mutate(f.values)}
                >
                  {translationObjects.appServer.t("Validate")}
                </DefaultButton>
                <Tooltip label={readonlyTooltip} disabled={!readonlyTooltip}>
                  <DefaultButton
                    disabled={readonlyTooltip.length > 0}
                    key="activate"
                    loading={activate.isPending}
                    onClick={() =>
                      activate.mutate({ id: id, isActive: !isActive })
                    }
                  >
                    {isActive
                      ? translationObjects.appServer.t("Deactivate")
                      : translationObjects.appServer.t("Activate")}
                  </DefaultButton>
                </Tooltip>
                <Tooltip disabled={!readonlyTooltip} label={readonlyTooltip}>
                  <PrimaryButton
                    disabled={readonlyTooltip.length > 0}
                    key="save"
                    type="submit"
                    loading={updateConfig.isPending}
                  >
                    {translationObjects.appServer.t("Save")}
                  </PrimaryButton>
                </Tooltip>
                <DottedMenu
                  key="menu"
                  items={[
                    {
                      children: translationObjects.appServer.t("Delete"),
                      disabled: readonlyTooltip.length > 0,
                      onClick: async () => {
                        await deleteConfig.mutateAsync({ id })
                        router.history.back()
                      },
                    },
                    {
                      children:
                        translationObjects.appServer.t("CopyToClipboard"),
                      onClick: async () => {
                        clipboard.copy(JSON.stringify(f.values))
                      },
                    },
                    {
                      children: translationObjects.appServer.t("VerbAndNoun", {
                        noun: "Ids",
                        verb: translationObjects.appServer.t(
                          showIds ? "Show" : "hide"
                        ),
                      }),
                      onClick: () => {
                        setShowIds((v) => !v)
                      },
                    },
                  ]}
                />
                ,
              </Group>
            </Group>
            <Tabs
              py="xs"
              defaultValue={data.nodes.length > 1 ? "workflow" : "general"}
            >
              <Tabs.List>
                <Tabs.Tab value="general">
                  {translationObjects.appServer.t("General")}
                </Tabs.Tab>
                <Tabs.Tab value="workflow">
                  {translationObjects.appServer.t("Workflow")}
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="general" py="xs" maw={1200}>
                <Stack gap="xl">
                  <Fieldset
                    disabled={readonlyTooltip.length > 0}
                    legend={
                      <Title order={3}>
                        {translationObjects.appServer.t("GeneralSettings")}
                      </Title>
                    }
                    flex={"1"}
                  >
                    <Stack>
                      <AbsenceConfigFormInputs f={f} _={_} />
                    </Stack>
                  </Fieldset>
                  <Fieldset
                    legend={
                      <Title order={3}>
                        {translationObjects.absences.t(
                          "LeaveRequestFormSettings"
                        )}
                      </Title>
                    }
                    flex="1"
                    disabled={readonlyTooltip.length > 0}
                  >
                    <Stack>
                      <Checkbox
                        description={translationObjects.absences.t(
                          "ConfigRequestDutyRosterDescription"
                        )}
                        label={translationObjects.appServer.t("ShowDutyRoster")}
                        {...f.getInputProps(
                          _.requestFormSettings.showDutyRoster._PATH_,
                          { type: "checkbox" }
                        )}
                      />
                      <Fieldset
                        legend={
                          <Title order={4}>
                            {translationObjects.absences.t("DescriptionField")}
                          </Title>
                        }
                        flex="1"
                        disabled={readonlyTooltip.length > 0}
                      >
                        <Stack>
                          <TextInput
                            label={translationObjects.appServer.t("Label")}
                            {...f.getInputProps(
                              _.requestFormSettings.descriptionField.label
                                ._PATH_
                            )}
                          />
                          <TextInput
                            label={translationObjects.appServer.t(
                              "Placeholder"
                            )}
                            {...f.getInputProps(
                              _.requestFormSettings.descriptionField.placeholder
                                ._PATH_
                            )}
                          />
                          <TextInput
                            label={translationObjects.appServer.t(
                              "Description"
                            )}
                            {...f.getInputProps(
                              _.requestFormSettings.descriptionField.description
                                ._PATH_
                            )}
                          />
                          <Checkbox
                            label={translationObjects.appServer.t(
                              "IsFieldRequired"
                            )}
                            {...f.getInputProps(
                              _.requestFormSettings.descriptionField.isRequired
                                ._PATH_,
                              { type: "checkbox" }
                            )}
                          />
                        </Stack>
                      </Fieldset>
                    </Stack>
                  </Fieldset>

                  <Fieldset
                    legend={
                      <Title order={3}>
                        {translationObjects.absences.t(
                          "GeneralApproverSettings"
                        )}
                      </Title>
                    }
                    disabled={readonlyTooltip.length > 0}
                  >
                    <Stack>
                      <Checkbox
                        description={translationObjects.absences.t(
                          "ConfigApprovalDutyRosterDescription"
                        )}
                        label={translationObjects.appServer.t("ShowDutyRoster")}
                        {...f.getInputProps(
                          _.generalApproverSettings.showDutyRoster._PATH_,
                          { type: "checkbox" }
                        )}
                      />
                    </Stack>
                  </Fieldset>
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="workflow" py="xs">
                <AbsenceConfigContextProvider
                  showIds={showIds}
                  canModify={canModify}
                  isReadonly={readonlyTooltip.length > 0}
                  validationErrors={validate.data ? validate.data.Fields : null}
                >
                  <ReactFlowProvider>
                    <ConfigFlow
                      data={{ edges: data.edges!, nodes: data.nodes! }}
                    />
                  </ReactFlowProvider>
                </AbsenceConfigContextProvider>
              </Tabs.Panel>
            </Tabs>
          </>
        )}
      </Form>
    </Page>
  )
}
